import { useCallback, useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import { getFeatured } from "@/lib/filmData";
import { TYPE_LABEL } from "@/types/film";

const slides = getFeatured(4);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      aria-label="焦点影视轮播"
      className="relative h-[560px] w-full overflow-hidden bg-primary-950 md:h-[640px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((film, slideIndex) => (
        <div
          key={film.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={slideIndex !== index}
        >
          <img
            src={film.cover}
            alt=""
            className="h-full w-full scale-110 object-cover object-top opacity-40 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/60"></div>
        </div>
      ))}

      <div className="page-shell relative flex h-full items-center pt-16 md:pt-20">
        {slides.map((film, slideIndex) => (
          <div
            key={film.id}
            className={`grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] ${
              slideIndex === index ? "animate-fade-in" : "hidden"
            }`}
          >
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
                  {TYPE_LABEL[film.type]}
                </span>
                <span className="flex items-center gap-1 rounded-md border border-background-50/25 px-2.5 py-1 text-xs font-medium text-background-100">
                  <i className="ri-fire-line text-accent-400" aria-hidden="true"></i>
                  热门度 {film.popularity}
                </span>
                <span className="flex items-center gap-1 text-xs font-medium text-accent-400">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {film.rating.toFixed(1)} 分
                </span>
              </div>

              <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 md:text-5xl">
                {film.title}
              </h1>

              <p className="mt-3 text-sm text-background-300">
                {film.year} · {film.region} · {film.genres.join(" / ")} · 导演 {film.director}
              </p>

              <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {film.summary}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${film.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
                  查看剧情详情
                </Link>
                <Link
                  to={`/category/${film.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  浏览{TYPE_LABEL[film.type]}频道
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex lg:justify-end">
              <div className="w-[300px] overflow-hidden rounded-lg border border-background-50/15">
                <div className="aspect-[2/3] w-full bg-primary-900">
                  <img
                    src={film.cover}
                    alt={`${film.title} 高清海报`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-6 left-0 right-0">
        <div className="page-shell flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((film, slideIndex) => (
              <button
                key={film.id}
                type="button"
                onClick={() => go(slideIndex)}
                aria-label={`切换到 ${film.title}`}
                className={`h-1.5 rounded-full transition-all ${
                  slideIndex === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              ></button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="上一部"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/25 text-lg text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="下一部"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/25 text-lg text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}